import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { ApiConfig, API_USER_ROUTER } from "../../../services/api-config";
import { useStoreOptions } from "../../../hooks/use-store-options";
import FaultTicketService, {
  FAULT_SEVERITY,
  FAULT_SEVERITY_LABEL,
  FAULT_TYPE,
  FAULT_TYPE_LABEL,
} from "../services/fault-ticket-service";
import SettingService, { SETTING_KEY } from "../../setting/services/setting-service";

interface EmployeeOption {
  id: number;
  name?: string;
  phone?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  // Prefill khi mở từ 1 đơn cụ thể (vd đơn giặt lại)
  defaultBranchId?: number | null;
  defaultOrderCode?: string;
}

const FaultTicketCreateModal: React.FC<Props> = ({ open, onClose, onCreated, defaultBranchId, defaultOrderCode }) => {
  const { options: branchOptions } = useStoreOptions();
  const { fetch: createFault, loading } = FaultTicketService.create();
  const { fetch: fetchSettings } = SettingService.getAll();

  const [branchId, setBranchId] = useState<number | "">("");
  const [orderId, setOrderId] = useState<string>("");
  const [faultType, setFaultType] = useState<number>(FAULT_TYPE.GIAO_TRE);
  const [severity, setSeverity] = useState<number>(FAULT_SEVERITY.MEDIUM);
  const [responsible, setResponsible] = useState<EmployeeOption[]>([]);
  const [description, setDescription] = useState("");
  // Nhập ĐIỂM phạt (không nhập tiền) — tiền do BE quy đổi theo CONFIG_POINT_TO_VND.
  const [points, setPoints] = useState<string>("");
  // Tỉ lệ quy đổi, chỉ dùng để hiển thị số tiền tương ứng cho người tạo phiếu biết trước.
  const [pointToVnd, setPointToVnd] = useState<number>(0);

  const reset = () => {
    setBranchId("");
    setOrderId("");
    setFaultType(FAULT_TYPE.GIAO_TRE);
    setSeverity(FAULT_SEVERITY.MEDIUM);
    setResponsible([]);
    setDescription("");
    setPoints("");
  };

  // Reset khi đóng; prefill branch + mã đơn khi mở từ 1 đơn cụ thể.
  useEffect(() => {
    if (!open) { reset(); return; }
    if (defaultBranchId) setBranchId(Number(defaultBranchId));
    if (defaultOrderCode) setOrderId(defaultOrderCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Nạp tỉ lệ điểm → VND để hiện xem trước. Lỗi/chưa cấu hình → 0, form vẫn dùng được (chỉ không hiện tiền).
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      try {
        const res = await fetchSettings();
        if (!mounted) return;
        const list = Array.isArray(res?.data) ? res!.data : [];
        const rate = Number(list.find((s) => s.key === SETTING_KEY.POINT_TO_VND)?.value);
        setPointToVnd(Number.isFinite(rate) && rate > 0 ? rate : 0);
      } catch {
        // giữ 0
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pointValue = Number(points) || 0;
  const previewAmount = pointValue > 0 && pointToVnd > 0 ? pointValue * pointToVnd : 0;

  if (!open) return null;

  const handleSubmit = async () => {
    if (!branchId) return showToast({ type: "warning", message: "Vui lòng chọn chi nhánh" });
    if (!responsible[0]?.id)
      return showToast({ type: "warning", message: "Vui lòng chọn nhân sự chịu trách nhiệm" });

    try {
      const res = await createFault({
        branch_id: Number(branchId),
        fault_type: faultType,
        severity,
        responsible_user_id: responsible[0].id,
        order_code: orderId.trim() ? orderId.trim() : undefined,
        description: description || undefined,
        penalty_points: pointValue > 0 ? pointValue : undefined,
      });
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã tạo phiếu lỗi" });
        reset();
        onCreated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Tạo phiếu lỗi thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Tạo phiếu lỗi thất bại" });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[560px] max-w-[95vw] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Tạo phiếu lỗi</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-lg">
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-600 mb-1">
                Chi nhánh <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                value={branchId}
                onChange={(e) => {
                  setBranchId(e.target.value ? Number(e.target.value) : "");
                  setResponsible([]); // đổi chi nhánh → bỏ chọn nhân sự cũ
                }}
              >
                <option value="">-- Chọn chi nhánh --</option>
                {branchOptions.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1">Mã đơn</label>
              <input
                type="text"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="VD: OD1001"
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-600 mb-1">
              Nhân sự chịu trách nhiệm <span className="text-red-500">*</span>
            </label>
            <SelectDropdownAsyncPaginate<EmployeeOption>
              key={`resp-${branchId || "all"}`}
              url={`${API_USER_ROUTER.API_LIST}`}
              projectId={ApiConfig.PROJECT_ID.USER_SERVICE}
              placeholder={branchId ? "Chọn nhân sự..." : "Chọn chi nhánh trước"}
              isMulti={false}
              disabled={!branchId}
              defaultOptions={responsible}
              onChange={(selected) => setResponsible(selected)}
              labelOption="name"
              classNames="w-full"
              params={{ status: 1, branch_id: branchId || undefined }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-600 mb-1">
                Loại lỗi <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                value={faultType}
                onChange={(e) => setFaultType(Number(e.target.value))}
              >
                {Object.entries(FAULT_TYPE_LABEL).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-600 mb-1">Mức độ</label>
              <select
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                value={severity}
                onChange={(e) => setSeverity(Number(e.target.value))}
              >
                {Object.entries(FAULT_SEVERITY_LABEL).map(([val, label]) => (
                  <option key={val} value={val}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-600 mb-1">Mô tả</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 h-24 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none"
              placeholder="Mô tả lỗi..."
            />
          </div>

          <div>
            <label className="block text-sm text-slate-600 mb-1">Điểm phạt (tuỳ chọn)</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={points}
                onChange={(e) => setPoints(e.target.value.replace(/\D/g, ""))}
                placeholder="VD: 2"
                className="w-32 border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              <span className="text-sm text-slate-500">điểm</span>
              {previewAmount > 0 && (
                <span className="text-sm font-medium text-green-700">
                  = {previewAmount.toLocaleString("vi-VN")}đ
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-400">
              {pointToVnd > 0
                ? `Quy đổi hiện tại: 1 điểm = ${pointToVnd.toLocaleString("vi-VN")}đ (Cài đặt hệ thống). Số tiền được chốt lúc tạo phiếu.`
                : "Chưa cấu hình quy đổi điểm → tiền ở Cài đặt hệ thống, phiếu sẽ chưa có số tiền phạt."}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
            Hủy
          </button>
          <button
            disabled={loading}
            onClick={handleSubmit}
            className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
          >
            {loading ? "Đang lưu..." : "Tạo phiếu"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaultTicketCreateModal;
